// components/nft/nft-grid-card.tsx
import { Edit, Eye, Trash2 } from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { NFT } from '@/types/nft';

interface NFTGridCardProps {
  nft: NFT;
  onEdit: (nft: NFT) => void;
  onDelete: (nft: NFT) => void;
  onView: (nft: NFT) => void;
}

export const NFTGridCard: React.FC<NFTGridCardProps> = ({
  nft,
  onEdit,
  onDelete,
  onView,
}) => {
  const getStatusColor = (status: NFT['status']) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-500';
      case 'minted':
        return 'bg-blue-500';
      case 'listed':
        return 'bg-green-500';
      case 'sold':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <Card key={nft.id} className="overflow-hidden group relative rounded-3xl">
      {/* Hover Controls */}
      <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          className="h-8 w-8 p-0 rounded-full"
          onClick={() => onEdit(nft)}
        >
          <Edit className="w-4 h-4 text-primary" />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="h-8 w-8 p-0 rounded-full"
          onClick={() => onDelete(nft)}
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="h-8 w-8 p-0  backdrop-blur-sm rounded-full"
          onClick={() => onView(nft)}
        >
          <Eye className="w-4 h-4" />
        </Button>
      </div>

      <div className="aspect-square overflow-hidden relative">
        <img
          src={nft.image}
          alt={nft.name}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
      </div>

      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold truncate flex-1">{nft.name}</h3>
          <Badge className={getStatusColor(nft.status)}>{nft.status}</Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {nft.description}
        </p>
        <div className="flex justify-between items-center mb-3">
          <span className="font-bold">
            {nft.price} {nft.currency}
          </span>
          <span className="text-sm text-muted-foreground">
            {nft.collection}
          </span>
        </div>

        {/* Attributes */}
        {nft.attributes.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {nft.attributes.slice(0, 2).map((attr, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {attr.trait_type}: {attr.value}
              </Badge>
            ))}
            {nft.attributes.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{nft.attributes.length - 2} more
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
